/**
 * useGame — wires the XState game actor to the app.
 *
 * Responsibilities:
 *   - Create the actor, resuming from the persisted snapshot in the session store if one exists.
 *   - Autosave: write the actor's persisted snapshot to the store on every transition (instant
 *     local resume; the backend sync layer hooks in here later).
 *   - Drive the clock: send a TICK every second during timed play so the machine can enforce the
 *     45-minute cut-off.
 */
import { useActorRef } from "@xstate/react";
import { useEffect } from "react";
import { gameMachine } from "./machine";
import { useSession } from "../store/session";

export function useGame() {
  // Read the saved snapshot once at mount (non-reactive) to seed the actor.
  const savedSnapshot = useSession.getState().runSnapshot;
  const actorRef = useActorRef(
    gameMachine,
    savedSnapshot ? { snapshot: savedSnapshot as never } : undefined,
  );

  useEffect(() => {
    const sub = actorRef.subscribe((snap) => {
      useSession.getState().saveSnapshot(actorRef.getPersistedSnapshot(), snap.matches("finished"));
    });
    return () => sub.unsubscribe();
  }, [actorRef]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const snap = actorRef.getSnapshot();
      if (snap.matches("playing") && snap.context.mode === "timed") {
        actorRef.send({ type: "TICK", ms: 1000 });
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [actorRef]);

  return actorRef;
}
